/* eslint-disable max-len */
import React, { type PropsWithChildren, useEffect, useState } from "react";

type Platform = "mac" | "windows" | "linux";

const detectPlatform = (): Platform => {
    if (typeof navigator === "undefined") {
        return "mac";
    }
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("mac")) {
        return "mac";
    }
    if (ua.includes("win")) {
        return "windows";
    }
    return "linux";
};

const MacWindowControls = () => (
    <div className="flex gap-2 md:gap-2.5">
        <div className="h-3.5 w-3.5 rounded-full bg-[#ff5f57] md:h-4 md:w-4" />
        <div className="h-3.5 w-3.5 rounded-full bg-[#febc2e] md:h-4 md:w-4" />
        <div className="h-3.5 w-3.5 rounded-full bg-[#28c840] md:h-4 md:w-4" />
    </div>
);

const WindowsWindowControls = () => (
    <div className="flex">
        <div className="flex h-5 w-8 items-center justify-center text-gray-500 hover:bg-gray-200 md:h-6 md:w-10">
            <svg width="10" height="1" viewBox="0 0 10 1" className="md:scale-110">
                <rect fill="currentColor" width="10" height="1" />
            </svg>
        </div>
        <div className="flex h-5 w-8 items-center justify-center text-gray-500 hover:bg-gray-200 md:h-6 md:w-10">
            <svg width="9" height="9" viewBox="0 0 9 9" fill="none" className="md:scale-110">
                <rect x="0.5" y="0.5" width="8" height="8" stroke="currentColor" strokeWidth="1" />
            </svg>
        </div>
        <div className="flex h-5 w-8 items-center justify-center text-gray-500 hover:bg-red-500 hover:text-white md:h-6 md:w-10">
            <svg width="10" height="10" viewBox="0 0 10 10" className="md:scale-110">
                <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.2" />
                <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.2" />
            </svg>
        </div>
    </div>
);

const LockIcon = () => (
    <svg className="h-3 w-3 text-gray-400 md:h-3.5 md:w-3.5" viewBox="0 0 16 16" fill="currentColor">
        <path
            fillRule="evenodd"
            d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 6V4.5a2 2 0 1 0-4 0V7h4Z"
            clipRule="evenodd"
        />
    </svg>
);

const NavArrow = ({ direction }: { direction: "back" | "forward" }) => (
    <svg className="h-3.5 w-3.5 text-gray-400 md:h-4 md:w-4" viewBox="0 0 16 16" fill="currentColor">
        {direction === "back" ? (
            <path
                d="M11 2L5 8l6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        ) : (
            <path
                d="M5 2l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        )}
    </svg>
);

type BrowserFrameProps = PropsWithChildren<{
    url?: string;
    className?: string;
}>;

export const BrowserFrame = ({ url, className = "", children }: BrowserFrameProps) => {
    const [platform, setPlatform] = useState<Platform>("mac");

    useEffect(() => {
        setPlatform(detectPlatform());
    }, []);

    const isMac = platform === "mac";

    return (
        <div
            className={`overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl md:rounded-xl ${className}`}
        >
            {/* Title bar */}
            <div className="flex items-center justify-between border-b border-gray-200 bg-[#dee1e6] px-3 py-2 md:px-4 md:py-2.5">
                {/* Left: macOS controls or nav buttons */}
                <div className="flex items-center gap-3 md:gap-4">
                    {isMac && <MacWindowControls />}
                    {!isMac && (
                        <div className="flex items-center gap-1">
                            <NavArrow direction="back" />
                            <NavArrow direction="forward" />
                        </div>
                    )}
                </div>

                {/* Center: URL bar */}
                {url && (
                    <div className="mx-2 flex min-w-0 flex-1 justify-center md:mx-4">
                        <div className="flex max-w-full items-center gap-1.5 truncate rounded-full bg-[#f1f3f4] px-3 py-1 text-xs text-gray-600 md:gap-2 md:px-4 md:py-1.5 md:text-sm">
                            <LockIcon />
                            <span className="truncate">{url}</span>
                        </div>
                    </div>
                )}

                {/* Right: Windows controls or spacer */}
                {!isMac ? (
                    <WindowsWindowControls />
                ) : (
                    <div className="flex items-center gap-1">
                        <NavArrow direction="back" />
                        <NavArrow direction="forward" />
                    </div>
                )}
            </div>

            {/* Content area */}
            <div className="px-4 pt-4 sm:px-6 md:py-6">{children}</div>
        </div>
    );
};
